import { useContext, useEffect, useState } from "react";
import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import { useTranslation } from "react-i18next";
import AttToken from "../../helpers/attToken";
import { TicketsV3 } from "../../service/Tickets";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Alert, Skeleton, Snackbar, Stack, TablePagination, Typography } from "@mui/material";
import { SpanChamado } from "./style/AllNotificationsStyle";
import RedirectListModal from "./Modals/RedirectListModal";
import TicketsFilters from "./TicketsFilters";
import { toast } from "react-toastify";
import { TicketsContext } from "../../context/TickestsContext";
import ModalTicket from "./Modals/ModalTicket";
import { trackEventMatomo } from "../../helpers/matomo";
import { useQuery } from "@tanstack/react-query";
import { getTicketStatusPresentation } from "../../helpers/ticketStatusPresentation";

const ticketsV3Service = new TicketsV3();

const normalizeTicketId = (ticketId: string) => ticketId.split('_-')[0];

export default function MyTickets() {
  const [openModal, setOpenModal] = useState(false);
  const { emitidos,
    userEmissor,
    expirado,
    modelos,
    modelosNovos,
    inicioRangeDe,
    inicioRangeAte,
    previsaoRangeDe,
    previsaoRangeAte,
    status,
    clientes,
    locais,
    conjuntos,
    unidades,
    ordenarPor,
    ordem,
    responsavel,
    exibeDuplicados,
  } = useContext(TicketsContext);

  const { t } = useTranslation('translation');
  const [open, setOpen] = useState(false);
  const [openEncaminhados, setOpenEncaminhados] = useState(false);
  const itensPerPage = 10;
  const [id, setId] = useState<string | null>(null);
  const [, setIndex] = useState<number[] | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageCursors, setPageCursors] = useState<Record<number, number | string | null>>({ 0: null });
  const [disabled, setDisabled] = useState(false);
  const [reset, setReset] = useState<number>(0);
  const [userNotResponsible, setUserNotResponsible] = useState<boolean>(false);
  const currentCursor = pageCursors[currentPage] ?? null;

  const { data, isLoading, isFetching, isSuccess, refetch, isError } = useQuery({
    queryKey: ['GET_TICKETS', currentPage, currentCursor, reset, emitidos],
    queryFn: async () => {
      setUserNotResponsible(false);
      toast.info(t('notifications.myTickets.filtringRequests'), { toastId: 'buscandoChamados' });
      const token = await AttToken();
      if (token) {
        const rangePrevisao = {
          de: previsaoRangeDe && new Date(previsaoRangeDe).setHours(0, 0, 0, 0),
          ate: previsaoRangeAte && new Date(previsaoRangeAte).setHours(23, 59, 59, 999)
        };
        const inicioRange = {
          de: inicioRangeDe && new Date(inicioRangeDe).setHours(0, 0, 0, 0),
          ate: inicioRangeAte && new Date(inicioRangeAte).setHours(23, 59, 59, 999)
        };
        const response = await ticketsV3Service.getAll(token, itensPerPage, currentPage,
          {
            userEmissor: userEmissor ? userEmissor.length > 0 && userEmissor.length <= 30 ? userEmissor?.map((ele) => ele.value) ?? null : null : null,
            expirado: JSON.parse(expirado),
            modelos: modelos && modelos.length > 0 ? modelos?.map((ele) => ele.value) ?? null : null,
            modelosNovos: modelosNovos && modelosNovos.length > 0 ? modelosNovos?.map((ele) => ele.value) ?? null : null,
            inicioRange: inicioRangeDe || inicioRangeAte ? inicioRange : null,
            previsaoRange: previsaoRangeDe || previsaoRangeAte ? rangePrevisao : null,
            status: status?.includes('all') ? undefined : status?.includes('em_andamento') ? ['emandamento', 'em_andamento'] : status && status.length > 0 ? status.includes('null') ? null : status : null,
            cliente: clientes ? clientes.length > 0 && clientes.length <= 30 ? clientes?.map((ele) => ele.value) ?? null : null : null,
            local: locais ? locais.length > 0 && locais.length <= 30 ? locais?.map((ele) => ele.value) ?? null : null : null,
            conjunto: conjuntos ? conjuntos.length > 0 && conjuntos.length <= 30 ? conjuntos?.map((ele) => ele.value) ?? null : null : null,
            unidade: unidades ? unidades.length > 0 && unidades.length <= 30 ? unidades?.map((ele) => ele.value) ?? null : null : null,
            ordenarPor: ordenarPor,
            ordem: ordem,
            responsavel: responsavel ? responsavel.length > 0 && responsavel.length <= 30 ? responsavel?.map((ele) => ele.value) ?? null : null : null,
            duplicado: exibeDuplicados
          },
          emitidos,
          currentCursor);
        if (!response?.success) {
          if (response?.message === 'Indices estão sendo criados, aguarde') {
            toast.warning(t('notifications.myTickets.creatingIndices'), { toastId: 'erro' });
          } else if (response?.message === 'Usuário não pertence a nenhuma equipe.') {
            setUserNotResponsible(true);
            toast.warning(t('notifications.myTickets.userNotResponsible'), { toastId: 'erro' });
          } else if (response?.error?.includes('Too many disjunctions')) {
            toast.error(t('notifications.myTickets.filterLimitReached'), { toastId: 'erro' });
          } else toast.error('Erro ao filtar chamados', { toastId: 'erro' });
          throw new Error('Erro ao buscar chamados');
        };
        return response.data;
      }
    },
    retry: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000
  });

  const tableOptions: MUIDataTableOptions = {
    serverSide: true,
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    viewColumns: false,
    print: false,
    download: false,
    filter: false,
    filterType: 'textField',
    responsive: 'vertical',
    rowsPerPageOptions: [10],
  };

  const columns = [
    {
      name: "id",
      label: 'ID',
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: string) => (
          normalizeTicketId(value)
        )
      }
    },
    {
      name: "inicio",
      label: t('notifications.ticket.createdAt'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => (
          new Date(value).toLocaleString()
        ),
      }
    },
    {
      name: 'usuarioNome',
      label: t('notifications.unread.user'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: string) => (
          value?.startsWith('anonimo') ? `${t('checklists.Modal.anonymous')}` :
            value
        ),
      }
    },
    {
      name: 'nome',
      label: t('notifications.ticket.tickets'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
      }
    },
    {
      name: 'responsavel',
      label: t('notifications.ticket.responsable'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
      }
    },
    {
      name: 'prioridade',
      label: t('notifications.ticket.priority'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: string) => {
          switch (value) {
            case 'alta':
              return (
                <span className="tag is-danger">{t('notifications.myTickets.high')}</span>
              );
            case 'media':
              return (
                <span className="tag is-warning">{t('notifications.myTickets.medium')}</span>
              );
            case 'baixa':
              return (
                <span className="tag is-primary">{t('notifications.myTickets.low')}</span>
              );
            default:
              return (
                <span className="tag is-info">NA</span>
              );
          }
        }
      }
    },
    {
      name: "dataPrevisao",
      label: t('notifications.ticket.limitDate'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => (
          value ?
            new Date(value).toLocaleString() : ''
        ),
      }
    },
    {
      name: "expirado",
      label: t('notifications.ticket.expired'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: boolean) => (
          value
            ? <span className="tag is-danger">{t('notifications.ticket.expired')}</span>
            : <span className="tag is-success">{t('notifications.ticket.onTime')}</span>
        ),
      }
    },
    {
      name: "status",
      label: t('status'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: string) => {
          const presentation = getTicketStatusPresentation(value);
          return presentation
            ? <span className={presentation.tagClassName}>{t(presentation.labelKey)}</span>
            : <span>{value || '-'}</span>;
        }
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    page: currentPage,
    rowsPerPage: itensPerPage,
    count: 1000000,
    pagination: false,
    onRowClick: (rowData) => {
      trackEventMatomo('meus chamados', 'click', 'row', 'abre chamado');
      setOpenModal(true);
      setId(rowData[0]);
    },
    search: false,
    sort: false,
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    }
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const getMuiTheme = () => {
    return createTheme({
      components: {
        MuiPaper: {
          styleOverrides: {
            root: {
              width: '100% !important'
            },
          }
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              padding: '8px'
            }
          }
        }
      }
    });
  };

  function Loading() {
    return (
      <Skeleton variant="rounded" height={400} width={'100%'} />
    );
  }

  useEffect(() => {
    setDisabled(!data?.lastIndex);
  }, [data?.lastIndex]);

  useEffect(() => {
    setCurrentPage(0);
    setPageCursors({ 0: null });
  }, [reset, emitidos]);

  const handlePageChange = (_event: unknown, page: number) => {
    trackEventMatomo('meus chamados', 'click', 'paginação', page < currentPage ? 'página anterior' : 'próxima página');
    if (page < currentPage) {
      setCurrentPage(page);
      return;
    }

    if (page > currentPage && data?.lastIndex !== null && data?.lastIndex !== undefined) {
      setPageCursors(previous => ({ ...previous, [page]: data.lastIndex }));
      setCurrentPage(page);
    }
  };

  function Tabela() {
    if (isSuccess && data?.objChamados) {
      return (
        <Stack direction='column' justifyContent='flex-start' spacing={1} alignItems='flex-end' width='100%'>
          <ThemeProvider theme={getMuiTheme()}>
            <MUIDataTable
              title={t('notifications.ticket.tabtitle')}
              data={data?.objChamados || []}
              columns={columns}
              options={{ ...tableOptions, ...translatedTextLabels }}
            />
          </ThemeProvider>
          <TablePagination
            component="div"
            count={data?.lastIndex ? (currentPage + 1) * itensPerPage + 1 : currentPage * itensPerPage + (data?.objChamados?.length || 0)}
            page={currentPage}
            rowsPerPage={itensPerPage}
            rowsPerPageOptions={[]}
            slotProps={{ actions: { nextButton: { disabled } } }}
            onPageChange={handlePageChange}
            onRowsPerPageChange={() => { }}
            labelDisplayedRows={() => `${t('notifications.myTickets.page')} ${currentPage + 1}`}
            labelRowsPerPage=""
          />
        </Stack>
      );
    }
    if (!data?.objChamados?.length) {
      return (
        <Typography color='textDisabled' variant='h5'>{t('notifications.myTickets.noTicketsFound')}</Typography>
      );
    }
    if (isError || (data?.status !== 200)) {
      return (
        <Typography color='textDisabled' variant='h5'>{t('notifications.myTickets.errorFetchingTickets')}</Typography>
      );
    }
  }

  useEffect(() => {
    if (isSuccess && data?.objChamados) {
      toast.success(t('notifications.myTickets.requestsFiltred'), { toastId: 'sucessoChamado' });
      if (data?.encaminhados?.length) {
        setOpen(true);
      }
    }
  }, [isLoading, isFetching, isSuccess, t, data, currentPage]);


  return (
    <>
      {openModal && <ModalTicket openModal={openModal} setOpenModal={setOpenModal} id={id} mutate={refetch} />}
      {openEncaminhados && (
        <RedirectListModal
          open={openEncaminhados}
          setOpen={setOpenEncaminhados}
          chamados={(typeof data === 'object' && data?.encaminhados) ?? []}
        />
      )}
      <Snackbar
        open={open}
        autoHideDuration={60000e10}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity="warning" sx={{ width: '100%' }}>
          <SpanChamado onClick={() => {
            trackEventMatomo('meus chamados', 'click', 'alerta', 'abre chamados encaminhados');
            setOpenEncaminhados(true);
          }}>
            {`${typeof data === 'object' && data?.encaminhados} ${typeof data === 'object' && data?.encaminhados?.length > 1
              ? `${t('notifications.mytickets.requests')}`
              : `${t('notifications.myTickets.request')}`} ${t('notifications.myTickets.forwardedToU')}`}
          </SpanChamado>
        </Alert>
      </Snackbar>
      <Stack direction='column' justifyContent='center' alignItems='center' spacing={2} width='100%' sx={{ paddingBottom: 2 }}>
        {isLoading || isFetching ? <Loading /> : userNotResponsible ?
          <Typography color='textDisabled' variant='h5'>{t('notifications.myTickets.userNotResponsible')}</Typography> :
          <>
            <TicketsFilters
              setReset={setReset}
              setCurrentPage={setCurrentPage}
              setIndex={setIndex}
              isLoading={isLoading || isFetching}
              setDisabled={setDisabled}
            />
            <Tabela />
          </>
        }
      </Stack>
    </>
  );
}